import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Phone, MapPin, Clock, Send, CheckCircle, User, MessageSquare } from 'lucide-react';
import PublicNavbar from '../components/PublicNavbar';
import Icon from '../components/Icon';
import useSEO from '../hooks/useSEO';
import { OrganizationStructuredData } from '../components/StructuredData';

const ContactPage = () => {
  const navigate = useNavigate();
  
  // SEO optimization
  useSEO({
    title: 'Kontakta oss - Your Therapist | AI-driven terapi 24/7',
    description: 'Har du frågor om vår AI-drivna terapi eller behöver support? Kontakta oss via formuläret eller mejl. Vi svarar inom 24 timmar.',
    keywords: 'kontakt, support, hjälp, frågor, AI-terapi, kundtjänst, Your Therapist',
    image: 'https://www.yourtherapist.se/logo512.png',
    url: 'https://www.yourtherapist.se/contact',
    type: 'website'
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    // Validate required fields
    if (!formData.email || !formData.message) {
      setError('E-post och meddelande är obligatoriska fält');
      setIsSubmitting(false);
      return;
    }

    try {
      // Use environment variable for API URL, fallback to production URL
      const apiUrl = process.env.REACT_APP_API_URL 
        ? `${process.env.REACT_APP_API_URL}/api/contact`
        : 'https://therapy-ai-backend-zqsx.onrender.com/api/contact';
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setIsSubmitted(true);
        setFormData({ name: '', email: '', message: '' });
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Ett fel uppstod när meddelandet skulle skickas');
      }
    } catch (error) {
      console.error('Error sending contact form:', error);
      setError('Ett fel uppstod när meddelandet skulle skickas. Försök igen senare.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-primary font-sans">
      <style jsx>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
        
        .font-inter {
          font-family: 'Inter', sans-serif;
        }
      `}</style>

      {/* Top Navigation Bar */}
      <PublicNavbar />

      {/* Hero Section */}
      <section className="pt-20 pb-16 w-full ai-bg font-inter">
        <div className="w-[100%] max-w-[512px] mx-auto px-custom-6">{/* Content container with max width */}
            <div className="text-center mb-6">
              <h1 className="text-4xl md:text-5xl font-bold text-primary mb-6">
                Kontakta oss
              </h1>
              <p className="text-xl text-secondary mx-auto leading-relaxed">
                Har du frågor om vår tjänst eller behöver hjälp? Vi finns här för att hjälpa dig.
              </p>
            </div>

            <div>
              {/* Direct Contact Info */}
              <div className="mb-8 bg-white p-6 rounded-2xl border border-brand-primary/20">
                <div className="text-center">
                  <div className="w-12 h-12 bg-brand-primary rounded-full flex items-center justify-center mx-auto mb-4">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-primary mb-2">Mejla oss direkt</h3>
                  <p className="text-secondary mb-3">
                    Föredrar du att skicka e-post? Du kan kontakta oss direkt på:
                  </p>
                  <a 
                    href="mailto:info@yourtherapist.se" 
                    className="inline-flex items-center gap-2 px-custom-4 py-custom-2 bg-brand-primary text-inverse rounded-lg hover:bg-brand-primary-dark transition-custom font-medium"
                  >
                    <Mail className="w-4 h-4" />
                    info@yourtherapist.se
                  </a>
                </div>
              </div>

              {/* Contact Form */}
              <div >
                <div className="bg-primary p-8 rounded-2xl shadow-custom-lg border border-light">
                  <h2 className="text-2xl font-bold text-primary mb-6">Skicka ett meddelande</h2>
                  
                  {isSubmitted ? (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 bg-success-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CheckCircle className="w-8 h-8 text-success-600" />
                      </div>
                      <h3 className="text-xl font-semibold text-primary mb-2">Meddelande skickat!</h3>
                      <p className="text-secondary mb-6">
                        Tack för ditt meddelande. Vi kommer att svara dig inom 24 timmar.
                      </p>
                      <button
                        onClick={() => setIsSubmitted(false)}
                        className="px-custom-6 py-custom-3 bg-brand-primary text-inverse rounded-xl font-semibold hover:bg-brand-primary-dark transition-custom duration-300"
                      >
                        Skicka nytt meddelande
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      {/* Name Field */}
                      <div>
                        <label htmlFor="name" className="block text-sm font-semibold text-primary mb-2">
                          Namn (valfritt)
                        </label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-secondary" />
                          <input
                            type="text"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            className="w-full pl-10 pr-4 py-3 border border-light rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-custom bg-primary text-primary"
                            placeholder="Ditt namn"
                          />
                        </div>
                      </div>

                      {/* Email Field */}
                      <div>
                        <label htmlFor="email" className="block text-sm font-semibold text-primary mb-2">
                          E-post *
                        </label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-secondary" />
                          <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            required
                            className="w-full pl-10 pr-4 py-3 border border-light rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-custom bg-primary text-primary"
                            placeholder="din@email.se"
                          />
                        </div>
                      </div>

                      {/* Message Field */}
                      <div>
                        <label htmlFor="message" className="block text-sm font-semibold text-primary mb-2">
                          Meddelande *
                        </label>
                        <div className="relative">
                          <MessageSquare className="absolute left-3 top-3 w-5 h-5 text-secondary" />
                          <textarea
                            id="message"
                            name="message"
                            value={formData.message}
                            onChange={handleInputChange}
                            required
                            rows={6}
                            className="w-full pl-10 pr-4 py-3 border border-light rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-custom resize-none bg-primary text-primary"
                            placeholder="Beskriv ditt ärende eller din fråga..."
                          />
                        </div>
                      </div>

                      {/* Error Message */}
                      {error && (
                        <div className="p-4 bg-error-50 border border-error-200 rounded-xl">
                          <p className="text-error-700 text-sm">{error}</p>
                        </div>
                      )}

                      {/* Submit Button */}
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full px-custom-6 py-custom-4 bg-brand-primary text-inverse rounded-xl font-semibold hover:bg-brand-primary-dark transition-custom duration-300 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSubmitting ? (
                          <>
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            Skickar...
                          </>
                        ) : (
                          <>
                            <Send className="w-5 h-5" />
                            Skicka meddelande
                          </>
                        )}
                      </button>

                      <p className="text-sm text-secondary text-center">
                        * Obligatoriska fält
                      </p>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Structured Data for SEO */}
        <OrganizationStructuredData />
    </div>
  );
};

export default ContactPage;
